import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PublicAuthIntent, PublicAuthVerifyPayload } from '../../services/publicAuthService';
import { usePublicAuth } from '../../contexts/PublicAuthContext';
import '../../styles/modern-modal.css';
import './PublicAuthModal.css';

type AuthTab = 'login' | 'signup';
type LoginMode = 'otp' | 'password';
type OtpStep = 'email' | 'code';

const INITIAL_FORM = {
    email: '',
    fullName: '',
    password: '',
    confirmPassword: '',
    code: '',
};

const CODE_LENGTH = 6;

const sanitizeEmail = (value: string) => value.trim().toLowerCase();

const PublicAuthModal: React.FC = () => {
    const { t } = useTranslation();
    const {
        modal,
        closeModal,
        requestCode,
        verifyCode,
        loginWithPassword,
        isAuthenticated,
        user,
    } = usePublicAuth();

    const [activeTab, setActiveTab] = useState<AuthTab>('login');
    const [loginMode, setLoginMode] = useState<LoginMode>('otp');
    const [otpStep, setOtpStep] = useState<OtpStep>('email');
    const [form, setForm] = useState(INITIAL_FORM);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<string | null>(null);
    const [otpCountdown, setOtpCountdown] = useState<number>(0);
    const [intent, setIntent] = useState<PublicAuthIntent>('LOGIN');

    const allowResend = otpCountdown <= 0;

    useEffect(() => {
        if (!modal.open) {
            setActiveTab('login');
            setLoginMode('otp');
            setOtpStep('email');
            setForm(INITIAL_FORM);
            setLoading(false);
            setError(null);
            setStatus(null);
            setOtpCountdown(0);
            return;
        }

        const derivedIntent: PublicAuthIntent = modal.intent || 'LOGIN';
        setIntent(derivedIntent);
        const initialTab: AuthTab = derivedIntent === 'SIGNUP' ? 'signup' : 'login';
        setActiveTab(initialTab);
        if (initialTab === 'login' && derivedIntent === 'LOGIN') {
            setLoginMode('otp');
        }
        setForm((prev) => ({
            ...INITIAL_FORM,
            email: modal.email ? sanitizeEmail(modal.email) : prev.email,
        }));
        setOtpStep('email');
        setLoading(false);
        setError(null);
        setStatus(null);
        setOtpCountdown(0);
    }, [modal]);

    useEffect(() => {
        if (!modal.open) return;
        if (otpCountdown <= 0) return;
        const timer = window.setTimeout(() => setOtpCountdown((prev) => Math.max(0, prev - 1)), 1000);
        return () => window.clearTimeout(timer);
    }, [otpCountdown, modal.open]);

    useEffect(() => {
        if (activeTab === 'signup') {
            setIntent('SIGNUP');
            setLoginMode('otp');
        } else if (activeTab === 'login' && loginMode === 'password') {
            setIntent('LOGIN');
        }
        setError(null);
        setStatus(null);
    }, [activeTab, loginMode]);

    const headline = useMemo(() => {
        if (activeTab === 'signup') return t('publicAuth.signupTitle');
        if (loginMode === 'password') return t('publicAuth.loginPasswordTitle');
        return t('publicAuth.loginTitle');
    }, [activeTab, loginMode, t]);

    const infoMessage = useMemo(() => {
        if (isAuthenticated && user) {
            return t('publicAuth.alreadyAuthenticated', { name: user.fullName || user.email });
        }
        if (activeTab === 'signup') return t('publicAuth.signupSubtitle');
        if (loginMode === 'password') return t('publicAuth.loginPasswordSubtitle');
        return t('publicAuth.loginSubtitle');
    }, [activeTab, loginMode, isAuthenticated, user, t]);

    const handleChange = (field: keyof typeof form, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const validateEmail = (value: string): boolean => {
        const email = sanitizeEmail(value);
        if (!email || !email.includes('@') || email.length < 5) {
            setError(t('publicAuth.errors.emailRequired'));
            return false;
        }
        return true;
    };

    const validatePassword = (value: string): boolean => {
        if (value.trim().length < 8) {
            setError(t('publicAuth.errors.passwordLength'));
            return false;
        }
        return true;
    };

    const handleRequestCode = async () => {
        if (!validateEmail(form.email)) return;
        if (activeTab === 'signup' && !form.fullName.trim()) {
            setError(t('publicAuth.errors.fullNameRequired'));
            return;
        }

        setLoading(true);
        setError(null);
        setStatus(null);

        try {
            const response = await requestCode(intent, sanitizeEmail(form.email));
            const ttl = typeof response.ttlSeconds === 'number' && response.ttlSeconds > 0
                ? response.ttlSeconds
                : 120;
            setOtpCountdown(ttl);
            setStatus(t('publicAuth.otp.sent'));
            setOtpStep('code');
        } catch (err: any) {
            setError(err?.message || t('publicAuth.errors.generic'));
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        if (!validateEmail(form.email)) return;
        const trimmedCode = form.code.trim();
        if (trimmedCode.length !== CODE_LENGTH) {
            setError(t('publicAuth.errors.codeInvalid', { digits: CODE_LENGTH }));
            return;
        }
        if (activeTab === 'signup' && !form.fullName.trim()) {
            setError(t('publicAuth.errors.fullNameRequired'));
            return;
        }
        if (activeTab === 'signup') {
            if (!validatePassword(form.password)) return;
            if (form.password.trim() !== form.confirmPassword.trim()) {
                setError(t('publicAuth.errors.passwordMismatch'));
                return;
            }
        } else if (form.password.trim()) {
            if (!validatePassword(form.password)) return;
        }

        setLoading(true);
        setError(null);
        setStatus(null);

        const payload: PublicAuthVerifyPayload = {
            intent,
            email: sanitizeEmail(form.email),
            code: trimmedCode,
        };

        if (activeTab === 'signup') {
            payload.fullName = form.fullName.trim();
            payload.password = form.password.trim();
        } else if (form.password.trim()) {
            payload.password = form.password.trim();
        }

        try {
            await verifyCode(payload);
        } catch (err: any) {
            setError(err?.message || t('publicAuth.errors.generic'));
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordLogin = async () => {
        if (!validateEmail(form.email)) return;
        if (!validatePassword(form.password)) return;

        setLoading(true);
        setError(null);
        setStatus(null);

        try {
            await loginWithPassword(sanitizeEmail(form.email), form.password.trim());
        } catch (err: any) {
            setError(err?.message || t('publicAuth.errors.generic'));
        } finally {
            setLoading(false);
        }
    };

    const renderTabs = () => (
        <div className="public-auth-modal__tabs">
            <button
                type="button"
                className={`public-auth-tab ${activeTab === 'login' ? 'public-auth-tab--active' : ''}`}
                onClick={() => setActiveTab('login')}
            >
                {t('publicAuth.tabs.login')}
            </button>
            <button
                type="button"
                className={`public-auth-tab ${activeTab === 'signup' ? 'public-auth-tab--active' : ''}`}
                onClick={() => setActiveTab('signup')}
            >
                {t('publicAuth.tabs.signup')}
            </button>
        </div>
    );

    const renderLoginSwitch = () => (
        <div className="public-auth-mode-switch">
            <button
                type="button"
                className={`public-auth-chip ${loginMode === 'otp' ? 'public-auth-chip--active' : ''}`}
                onClick={() => setLoginMode('otp')}
            >
                {t('publicAuth.loginOtpLabel')}
            </button>
            <button
                type="button"
                className={`public-auth-chip ${loginMode === 'password' ? 'public-auth-chip--active' : ''}`}
                onClick={() => setLoginMode('password')}
            >
                {t('publicAuth.loginPasswordLabel')}
            </button>
        </div>
    );

    const renderOtpStep = () => (
        <>
            {otpStep === 'email' && (
                <>
                    {activeTab === 'signup' && (
                        <label className="public-auth-field">
                            <span>{t('publicAuth.fields.fullName')}</span>
                            <input
                                type="text"
                                value={form.fullName}
                                onChange={(event) => handleChange('fullName', event.target.value)}
                                placeholder={t('publicAuth.placeholders.fullName')}
                            />
                        </label>
                    )}
                    <label className="public-auth-field">
                        <span>{t('publicAuth.fields.email')}</span>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(event) => handleChange('email', event.target.value)}
                            placeholder={t('publicAuth.placeholders.email')}
                        />
                    </label>
                    <button
                        type="button"
                        className="public-auth-primary"
                        onClick={handleRequestCode}
                        disabled={loading}
                    >
                        {loading ? t('publicAuth.actions.sending') : t('publicAuth.actions.sendCode')}
                    </button>
                </>
            )}

            {otpStep === 'code' && (
                <>
                    <div className="public-auth-note">
                        {status ? status : t('publicAuth.otp.instructions', { email: form.email })}
                    </div>
                    <label className="public-auth-field">
                        <span>{t('publicAuth.fields.code', { digits: CODE_LENGTH })}</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={CODE_LENGTH}
                            value={form.code}
                            onChange={(event) => handleChange('code', event.target.value.replace(/\D/g, ''))}
                            placeholder={'•'.repeat(CODE_LENGTH)}
                        />
                    </label>
                    {activeTab === 'signup' && (
                        <>
                            <label className="public-auth-field">
                                <span>{t('publicAuth.fields.password')}</span>
                                <input
                                    type="password"
                                    value={form.password}
                                    onChange={(event) => handleChange('password', event.target.value)}
                                    placeholder={t('publicAuth.placeholders.password')}
                                />
                            </label>
                            <label className="public-auth-field">
                                <span>{t('publicAuth.fields.confirmPassword')}</span>
                                <input
                                    type="password"
                                    value={form.confirmPassword}
                                    onChange={(event) => handleChange('confirmPassword', event.target.value)}
                                    placeholder={t('publicAuth.placeholders.confirmPassword')}
                                />
                            </label>
                        </>
                    )}
                    {activeTab === 'login' && (
                        <label className="public-auth-field">
                            <span>{t('publicAuth.fields.newPasswordOptional')}</span>
                            <input
                                type="password"
                                value={form.password}
                                onChange={(event) => handleChange('password', event.target.value)}
                                placeholder={t('publicAuth.placeholders.passwordOptional')}
                            />
                        </label>
                    )}
                    <button
                        type="button"
                        className="public-auth-primary"
                        onClick={handleVerifyCode}
                        disabled={loading}
                    >
                        {loading ? t('publicAuth.actions.verifying') : t('publicAuth.actions.verifyCode')}
                    </button>
                    <button
                        type="button"
                        className="public-auth-link"
                        disabled={!allowResend || loading}
                        onClick={handleRequestCode}
                    >
                        {allowResend
                            ? t('publicAuth.actions.resendCode')
                            : t('publicAuth.otp.resendCountdown', { seconds: otpCountdown })}
                    </button>
                </>
            )}
        </>
    );

    const renderPasswordLogin = () => (
        <>
            <label className="public-auth-field">
                <span>{t('publicAuth.fields.email')}</span>
                <input
                    type="email"
                    value={form.email}
                    onChange={(event) => handleChange('email', event.target.value)}
                    placeholder={t('publicAuth.placeholders.email')}
                />
            </label>
            <label className="public-auth-field">
                <span>{t('publicAuth.fields.password')}</span>
                <input
                    type="password"
                    value={form.password}
                    onChange={(event) => handleChange('password', event.target.value)}
                    placeholder={t('publicAuth.placeholders.password')}
                />
            </label>
            <button
                type="button"
                className="public-auth-primary"
                onClick={handlePasswordLogin}
                disabled={loading}
            >
                {loading ? t('publicAuth.actions.loggingIn') : t('publicAuth.actions.login')}
            </button>
        </>
    );

    if (!modal.open) return null;

    return (
        <div className="public-auth-overlay" role="dialog" aria-modal="true">
            <div className="public-auth-modal">
                <div className="public-auth-modal__header">
                    {renderTabs()}
                    <button type="button" className="public-auth-close" onClick={closeModal} aria-label={t('publicAuth.actions.close')}>
                        ×
                    </button>
                </div>

                <div className="public-auth-modal__body">
                    <h2>{headline}</h2>
                    <p className="public-auth-subtitle">{infoMessage}</p>

                    {activeTab === 'login' && renderLoginSwitch()}

                    <div className="public-auth-form">
                        {loginMode === 'password' ? renderPasswordLogin() : renderOtpStep()}
                    </div>

                    {error && <div className="public-auth-error" role="alert">{error}</div>}
                </div>
            </div>
        </div>
    );
};

export default PublicAuthModal;
